import React, { useState, useEffect } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CFormLabel, CFormInput, CFormTextarea, CFormSelect, CRow, CCol, CAlert,
} from '@coreui/react';
import { toast } from 'react-toastify';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { isValidEmailInput, formatPhoneInput, isValidPhoneInput, phoneForApi, PHONE_PREFIX } from 'src/utils/inputValidation';
import { isValidEmailFormat } from 'src/utils/email';
import {
	invalidateLettersList, patchCachedSession, invalidateCachedSupportHistory, invalidateCachedRequest,
} from 'src/utils/supportRequestCache';
import SupportStatusCascadeModal from './SupportStatusCascadeModal';
import { useSupportChannels } from 'src/hooks/useSupportChannels';

const collectCascadeRequests = (requests, adminRequestId) => {
	const idx = requests.findIndex(r => r.id === adminRequestId);
	if (idx < 0) return [];
	const result = [];
	for (let i = idx - 1; i >= 0; i--) {
		if (requests[i].sender_type === 'admin') break;
		result.push(requests[i]);
	}
	return result;
};

const SupportLetterReplyModal = ({
	open,
	onClose,
	parentRequest,
	sessionId,
	subject,
	userEmail,
	sessionRequests,
	onSuccess,
	onStatusApplied,
	clientData,
	adminData,
}) => {
	const channels = useSupportChannels();
	const [senderType, setSenderType] = useState('user');
	const [autoFillAsked, setAutoFillAsked] = useState(false);
	const [replyChannelId, setReplyChannelId] = useState('');
	const [replyEmail, setReplyEmail] = useState('');
	const [replyName, setReplyName] = useState('');
	const [replyPhone, setReplyPhone] = useState(PHONE_PREFIX);
	const [replySentAt, setReplySentAt] = useState('');
	const [replyMessage, setReplyMessage] = useState('');
	const [replySubmitting, setReplySubmitting] = useState(false);
	const [replyErrors, setReplyErrors] = useState({});
	const [alreadyRepliedConfirmed, setAlreadyRepliedConfirmed] = useState(false);
	const [cascadeOpen, setCascadeOpen] = useState(false);
	const [cascadeRequests, setCascadeRequests] = useState([]);

	useEffect(() => {
		if (open) {
			setSenderType('user');
			setAutoFillAsked(false);
			setReplyChannelId('');
			setReplyEmail('');
			setReplyName('');
			setReplyPhone(PHONE_PREFIX);
			setReplySentAt('');
			setReplyMessage('');
			setReplyErrors({});
			setAlreadyRepliedConfirmed(false);
			setCascadeOpen(false);
			setCascadeRequests([]);
		}
	}, [open]);

	const hasAdminReply = (sessionRequests || []).some(
		r => r.parent_id === parentRequest?.id && r.sender_type === 'admin'
	);

	const handleAutoFill = () => {
		setReplyEmail(clientData?.user_email || '');
		setReplyName(clientData?.user_name || '');
		setReplyPhone(formatPhoneInput(clientData?.user_phone || '') || PHONE_PREFIX);
		setAutoFillAsked(true);
	};

	const handleAutoFillAdmin = () => {
		setReplyEmail(adminData?.email || adminData?.username || '');
		setReplyName([adminData?.first_name, adminData?.last_name, adminData?.username].filter(Boolean).join(' ') || '');
		setReplyPhone(formatPhoneInput(adminData?.phone || '') || PHONE_PREFIX);
		setAutoFillAsked(true);
	};

	// Автозаповнення при перемиканні типу в post-autofill стані (форма вже відкрита)
	const handleSelectSenderType = (type) => {
		setSenderType(type);
		if (type === 'user') {
			setReplyEmail(clientData?.user_email || '');
			setReplyName(clientData?.user_name || '');
			setReplyPhone(formatPhoneInput(clientData?.user_phone || '') || PHONE_PREFIX);
		} else {
			setReplyEmail(adminData?.email || adminData?.username || '');
			setReplyName([adminData?.first_name, adminData?.last_name, adminData?.username].filter(Boolean).join(' ') || '');
			setReplyPhone(formatPhoneInput(adminData?.phone || '') || PHONE_PREFIX);
		}
	};

	const handleReplySubmit = async () => {
		const newErrors = {
			channelId: !replyChannelId,
			email: replyEmail.trim() !== '' && !isValidEmailFormat(replyEmail),
			name: !replyName.trim(),
			phone: !isValidPhoneInput(replyPhone),
			sentAt: !replySentAt,
			message: !replyMessage.trim(),
		};
		if (Object.values(newErrors).some(Boolean)) {
			setReplyErrors(newErrors);
			const missing = [
				newErrors.channelId && 'Канал',
				newErrors.email && 'Email (невірний формат)',
				newErrors.name && "Ім'я",
				newErrors.phone && 'Телефон (невірний формат)',
				newErrors.sentAt && 'Дата отримання',
				newErrors.message && 'Повідомлення',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setReplyErrors({});
		setReplySubmitting(true);

		try {
			const res = await AxiosInstance.post('/support-requests', {
				user_email: replyEmail.trim(),
				user_name: replyName.trim(),
				user_phone: phoneForApi(replyPhone),
				channel_id: parseInt(replyChannelId),
				sent_at: replySentAt,
				message: replyMessage.trim(),
				sender_type: senderType,
				parent_id: parentRequest?.id || null,
				session_id: sessionId,
				subject: subject || '',
				...(senderType === 'admin' && { status: 'replied' }),
			});
			const newRequest = res.data;
			onSuccess?.(newRequest);
			invalidateLettersList();
			if (sessionId) {
				patchCachedSession(sessionId, s => ({ ...s, requests: [...(s.requests || []), newRequest] }));
			}
			if (userEmail) invalidateCachedSupportHistory(userEmail);
			if (parentRequest?.id) invalidateCachedRequest(parentRequest.id);
			onClose();
			toast.success('Лист додано');
			if (senderType === 'admin') {
				const allRequests = [...(sessionRequests || []), newRequest];
				const cascade = collectCascadeRequests(allRequests, newRequest.id);
				if (cascade.length > 0) {
					setCascadeRequests(cascade);
					setCascadeOpen(true);
				}
			}
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження листа');
		} finally {
			setReplySubmitting(false);
		}
	};

	const modalTitle = parentRequest
		? `Відповідь на лист #${parentRequest.id}${parentRequest.user_email ? ' від ' + parentRequest.user_email : ''}`
		: `Новий лист до сесії #${sessionId}${subject ? ' «' + subject + '»' : ''}`;

	return (
		<>
			<CModal visible={open} onClose={() => !replySubmitting && onClose()} size="lg">
				<CModalHeader>
					<CModalTitle>{modalTitle}</CModalTitle>
				</CModalHeader>
				<CModalBody>
					{hasAdminReply && !alreadyRepliedConfirmed ? (
						<CAlert color="warning">
							На цей лист вже була дана відповідь. Ви хочете дати ще одну?
						</CAlert>
					) : (
						<FormSubmitOverlay submitting={replySubmitting}>
							{!autoFillAsked && (
								<div className="mb-3 p-3 bg-light rounded">
									<div className="text-muted small mb-3">
										Оберіть, від чийого імені додається лист — клієнта чи адміна, — а потім вкажіть, чи підставити дані автоматично або заповнити вручну.
									</div>
									<div className="mb-2 fw-semibold">Тип відправника:</div>
									<div className="d-flex gap-2 mb-3">
										<CButton
											color={senderType === 'user' ? 'info' : 'secondary'}
											variant={senderType === 'user' ? undefined : 'outline'}
											className={senderType === 'user' ? 'fw-bold border border-2' : ''}
											size="sm"
											onClick={() => setSenderType('user')}
										>
											Юзер
										</CButton>
										<CButton
											color={senderType === 'admin' ? 'warning' : 'secondary'}
											variant={senderType === 'admin' ? undefined : 'outline'}
											className={senderType === 'admin' ? 'fw-bold border border-2' : ''}
											size="sm"
											onClick={() => setSenderType('admin')}
										>
											Адмін
										</CButton>
									</div>
									<div className="text-muted small mb-2">Заповнити дані автоматично?</div>
									<div className="d-flex gap-2">
										{senderType === 'user' ? (
											<>
												<CButton
													color="primary"
													className="fw-bold border border-2"
													size="sm"
													onClick={handleAutoFill}
													title="Підставити відомі дані клієнта"
												>
													Так, дані клієнта
												</CButton>
												<CButton
													color="secondary"
													variant="outline"
													size="sm"
													onClick={() => setAutoFillAsked(true)}
													title="Заповнити вручну"
												>
													Ні, вручну
												</CButton>
											</>
										) : (
											<>
												<CButton
													color="warning"
													className="fw-bold border border-2"
													size="sm"
													onClick={handleAutoFillAdmin}
													title="Підставити дані адміна з профілю"
												>
													Так, мої дані
												</CButton>
												<CButton
													color="secondary"
													variant="outline"
													size="sm"
													onClick={() => setAutoFillAsked(true)}
													title="Заповнити вручну"
												>
													Ні, вручну
												</CButton>
											</>
										)}
									</div>
								</div>
							)}
							{autoFillAsked && (
								<CRow className="g-3">
									<CCol md={12} className="d-flex gap-2 align-items-center">
										<strong>Відправник:</strong>
										<CButton
											color={senderType === 'user' ? 'info' : 'warning'}
											size="sm"
											disabled
										>
											{senderType === 'user' ? 'Юзер' : 'Адмін'}
										</CButton>
										<CButton
											color="secondary"
											variant="outline"
											size="sm"
											onClick={() => {
												setAutoFillAsked(false);
												setReplyChannelId('');
												setReplyEmail('');
												setReplyName('');
												setReplyPhone(PHONE_PREFIX);
												setReplyErrors({});
											}}
										>
											Змінити
										</CButton>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Email</CFormLabel>
										<CFormInput
											value={replyEmail}
											onChange={e => {
												const val = e.target.value;
												if (isValidEmailInput(val)) {
													setReplyEmail(val);
													if (replyErrors.email) setReplyErrors(p => ({ ...p, email: false }));
												}
											}}
											invalid={!!replyErrors.email}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Ім'я *</CFormLabel>
										<CFormInput
											value={replyName}
											onChange={e => { setReplyName(e.target.value); if (replyErrors.name) setReplyErrors(p => ({ ...p, name: false })); }}
											invalid={!!replyErrors.name}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Телефон</CFormLabel>
										<CFormInput
											value={replyPhone}
											onChange={e => {
												const formatted = formatPhoneInput(e.target.value);
												if (formatted !== null) {
													setReplyPhone(formatted);
													if (replyErrors.phone) setReplyErrors(p => ({ ...p, phone: false }));
												}
											}}
											invalid={!!replyErrors.phone}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={6}>
										<CFormLabel>Дата і час відправки *</CFormLabel>
										<CFormInput
											type="datetime-local"
											value={replySentAt}
											onChange={e => { setReplySentAt(e.target.value); if (replyErrors.sentAt) setReplyErrors(p => ({ ...p, sentAt: false })); }}
											invalid={!!replyErrors.sentAt}
										/>
									</CCol>
									<CCol md={6}>
										<CFormLabel>Канал *</CFormLabel>
										<CFormSelect
											value={replyChannelId}
											onChange={e => {
												setReplyChannelId(e.target.value);
												if (replyErrors.channelId) setReplyErrors(p => ({ ...p, channelId: false }));
											}}
											invalid={!!replyErrors.channelId}
										>
											<option value="">— Оберіть канал —</option>
											{channels.map(ch => (
												<option key={ch.id} value={ch.id}>{ch.name}</option>
											))}
										</CFormSelect>
									</CCol>
									<CCol xs={12}>
										<CFormLabel>Текст листа *</CFormLabel>
										<CFormTextarea
											rows={5}
											value={replyMessage}
											onChange={e => { setReplyMessage(e.target.value); if (replyErrors.message) setReplyErrors(p => ({ ...p, message: false })); }}
											invalid={!!replyErrors.message}
											placeholder="Текст повідомлення..."
										/>
									</CCol>
								</CRow>
							)}
						</FormSubmitOverlay>
					)}
				</CModalBody>
				<CModalFooter>
					{hasAdminReply && !alreadyRepliedConfirmed ? (
						<>
							<CButton color="warning" onClick={() => setAlreadyRepliedConfirmed(true)} title="Підтвердити ще одну відповідь">
								Так, ще одна відповідь
							</CButton>
							<CButton color="secondary" variant="outline" onClick={onClose} title="Скасувати">
								Скасувати
							</CButton>
						</>
					) : (
						<>
							{autoFillAsked && (
								<CButton
									color="primary"
									onClick={handleReplySubmit}
									disabled={replySubmitting}
									title="Зберегти лист"
								>
									{replySubmitting ? 'Збереження...' : 'Зберегти лист'}
								</CButton>
							)}
							<CButton
								color="secondary"
								variant="outline"
								onClick={onClose}
								disabled={replySubmitting}
								title="Скасувати"
							>
								Скасувати
							</CButton>
						</>
					)}
				</CModalFooter>
			</CModal>

			<SupportStatusCascadeModal
				open={cascadeOpen}
				userRequests={cascadeRequests}
				sessionId={sessionId}
				userEmail={userEmail}
				onStatusApplied={onStatusApplied}
				onClose={() => { setCascadeOpen(false); setCascadeRequests([]); }}
			/>
		</>
	);
};

export default SupportLetterReplyModal;
